/**
 * Task #59 — Status Admin Dialog
 * Lets workspace admins add, rename, reorder, and delete schedule statuses.
 * "Day Off" (isSystemLocked=true) is always shown but cannot be deleted.
 * Supports drag-to-reorder with sortOrder persistence.
 */
import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Trash2, GripVertical, Lock } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface WorkspaceStatus {
  id: string;
  label: string;
  color: string;
  isSystemLocked: boolean;
  sortOrder: number;
}

interface StatusAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  statuses: WorkspaceStatus[];
}

export function StatusAdminDialog({ open, onOpenChange, statuses }: StatusAdminDialogProps) {
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [localOrder, setLocalOrder] = useState<WorkspaceStatus[]>([]);
  const dragIndex = useRef<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/schedule-statuses"] });

  // Use localOrder (optimistic drag state) when available, otherwise sorted from props
  const sorted = localOrder.length > 0
    ? localOrder
    : [...statuses].sort((a, b) => a.sortOrder - b.sortOrder);

  const createMutation = useMutation({
    mutationFn: async (label: string) => {
      const resp = await apiRequest("POST", "/api/schedule-statuses", { label, color: "#6366f1", sortOrder: statuses.length });
      return resp.json();
    },
    onSuccess: () => { invalidate(); setNewLabel(""); setLocalOrder([]); toast({ title: "Status added" }); },
    onError: () => toast({ title: "Error", description: "Failed to add status.", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, label, sortOrder }: { id: string; label?: string; sortOrder?: number }) => {
      const body: Record<string, any> = {};
      if (label !== undefined) body.label = label;
      if (sortOrder !== undefined) body.sortOrder = sortOrder;
      const resp = await apiRequest("PATCH", `/api/schedule-statuses/${id}`, body);
      return resp.json();
    },
    onSuccess: () => { invalidate(); setEditingId(null); },
    onError: () => toast({ title: "Error", description: "Failed to update status.", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/schedule-statuses/${id}`, {});
    },
    onSuccess: () => { invalidate(); setLocalOrder([]); toast({ title: "Status deleted" }); },
    onError: () => toast({ title: "Error", description: "Failed to delete status.", variant: "destructive" }),
  });

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    createMutation.mutate(newLabel.trim());
  };

  const handleStartEdit = (status: WorkspaceStatus) => {
    setEditingId(status.id);
    setEditingLabel(status.label);
  };

  const handleSaveEdit = () => {
    if (editingId && editingLabel.trim()) {
      updateMutation.mutate({ id: editingId, label: editingLabel.trim() });
      toast({ title: "Status updated" });
    }
  };

  // ── Drag-to-reorder ──────────────────────────────────────────────────────
  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === index) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(dragIndex.current, 1);
    reordered.splice(index, 0, moved);
    dragIndex.current = index;
    setLocalOrder(reordered);
  };

  const handleDrop = () => {
    if (!localOrder.length) return;
    for (let i = 0; i < localOrder.length; i++) {
      if (localOrder[i].sortOrder !== i) {
        updateMutation.mutate({ id: localOrder[i].id, sortOrder: i });
      }
    }
    setLocalOrder(localOrder.map((s, i) => ({ ...s, sortOrder: i })));
    dragIndex.current = null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Schedule Statuses</DialogTitle>
          <DialogDescription>
            Add, rename, or remove schedule status options. Drag rows to reorder. "Day Off" is system-locked and cannot be deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
          {sorted.map((status, index) => (
            <div
              key={status.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={handleDrop}
              className="flex items-center gap-2 p-2 rounded-md border border-border bg-card hover:bg-muted/30 group cursor-grab active:cursor-grabbing"
              data-testid={`status-row-${status.id}`}
            >
              <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />

              {editingId === status.id ? (
                <Input
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveEdit();
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  className="h-7 text-sm flex-1"
                  autoFocus
                  data-testid="input-status-edit"
                />
              ) : (
                <span
                  className="flex-1 text-sm cursor-pointer select-none"
                  onDoubleClick={() => !status.isSystemLocked && handleStartEdit(status)}
                  data-testid={`status-label-${status.id}`}
                >
                  {status.label}
                </span>
              )}

              {status.isSystemLocked ? (
                <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              ) : (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {editingId === status.id ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs"
                      onClick={handleSaveEdit}
                      disabled={updateMutation.isPending}
                      data-testid="button-save-status-edit"
                    >
                      {updateMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs"
                      onClick={() => handleStartEdit(status)}
                      data-testid={`button-edit-status-${status.id}`}
                    >
                      Edit
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1 text-destructive hover:text-destructive"
                    onClick={() => deleteMutation.mutate(status.id)}
                    disabled={deleteMutation.isPending}
                    data-testid={`button-delete-status-${status.id}`}
                  >
                    {deleteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                  </Button>
                </div>
              )}
            </div>
          ))}

          {sorted.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No statuses yet. Add one below.</p>
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t border-border">
          <Input
            placeholder="New status name..."
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            className="flex-1 h-8 text-sm"
            data-testid="input-new-status"
          />
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={!newLabel.trim() || createMutation.isPending}
            className="h-8"
            data-testid="button-add-status"
          >
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
